import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  buildTenantSchemaPlan,
  dryRunTenantSchemaPlan,
  getTenantSchemaPlan,
  getTenantSchemaVersions,
  getLatestTenantSchemaBaseline,
  approveTenantSchemaPlan,
  previewTenantSchemaRun,
  startTenantSchemaRun,
  getTenantSchemaRuns,
  cancelTenantSchemaRun,
} from '../api/tenantSchemaApi';
import { useAuth } from '../auth/AuthContext';
import { hasPermission } from '../auth/permissions';
import { colorTokens } from '../app/designTokens';
import { DataTable } from './DataTable';
import { DetailField } from './InfoCard';
import { LoadingSkeleton } from './LoadingSkeleton';
import { SectionCard } from './SectionCard';
import { StatusBadge } from './StatusBadge';
import { Button } from './ui/Button';
import { formatProvisioningMode } from '../utils/systemLabels';

const { semantic } = colorTokens;

function resolveSchemaApiError(err, t) {
  const payload = err?.response?.data || {};
  const rawKey = payload.message_key;
  if (rawKey) {
    const i18nKey = String(rawKey).startsWith('tenantSchema.')
      ? `tenantSchema:${String(rawKey).slice('tenantSchema.'.length)}`
      : (String(rawKey).includes(':') ? rawKey : `tenantSchema:errors.${rawKey}`);
    return t(i18nKey, {
      defaultValue: payload.message || t('tenantSchema:tenant_tab.action_failed'),
    });
  }
  return payload.message || t('tenantSchema:tenant_tab.action_failed');
}

export function TenantSchemaTab({ tenantId }) {
  const { t } = useTranslation(['tenantSchema', 'tenantSchemaRunner', 'common', 'statuses']);
  const { session } = useAuth();
  const canManage = hasPermission(session, 'system.tenant_schema.manage');
  const canApprove = hasPermission(session, 'system.tenant_schema.approve');
  const canRun = hasPermission(session, 'system.tenants.schema_runner.run') || canManage;
  const canApply = hasPermission(session, 'system.tenants.schema_runner.apply') || canManage;

  const [plan, setPlan] = useState(null);
  const [runs, setRuns] = useState([]);
  const [versions, setVersions] = useState([]);
  const [baseline, setBaseline] = useState(null);
  const [runnerConfig, setRunnerConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [planRes, runsRes, versionsRes, baselineRes] = await Promise.all([
        getTenantSchemaPlan(tenantId).catch(() => null),
        getTenantSchemaRuns(tenantId, { per_page: 5 }).catch(() => ({ data: [] })),
        getTenantSchemaVersions({ per_page: 50 }).catch(() => ({ data: [] })),
        getLatestTenantSchemaBaseline().catch(() => null),
      ]);
      setPlan(planRes?.data || planRes);
      setRuns(runsRes?.data || []);
      setVersions(versionsRes?.data || []);
      setBaseline(baselineRes?.data || null);
    } catch {
      setPlan(null);
      setVersions([]);
      setBaseline(null);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const latestRun = runs[0];
  const runnerEnabled = runnerConfig?.enabled ?? false;
  const hasSchemaVersion = versions.length > 0;
  const baselineApproved = baseline?.status === 'approved';
  const baselineNeedsReview = hasSchemaVersion && !baselineApproved;
  const buildDisabled = !hasSchemaVersion;
  const applyBlocked = !runnerEnabled || !(plan && ['approved', 'executed'].includes(plan.status)) || !baselineApproved;

  const runAction = async (action) => {
    setMessage('');
    setError('');
    try {
      const result = await action();
      if (result?.data?.enabled !== undefined) {
        setRunnerConfig(result.data);
      }
      setMessage(t('tenantSchema:tenant_tab.action_success'));
      load();
    } catch (err) {
      setError(resolveSchemaApiError(err, t));
    }
  };

  const loadPreview = async () => {
    const res = await previewTenantSchemaRun(tenantId, { mode: 'dry_run' });
    setRunnerConfig({
      enabled: res.data?.valid,
      ...res.data,
    });
  };

  useEffect(() => {
    if (canRun) {
      loadPreview().catch(() => {});
    }
  // Intentionally only re-run when tenant/permissions change; preview is best-effort.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRun, tenantId]);

  if (loading) return <LoadingSkeleton type="Detail" />;

  return (
    <Stack spacing={2}>
      {message && <Alert severity="success">{message}</Alert>}
      {error && <Alert severity="error">{error}</Alert>}

      {!hasSchemaVersion && (
        <Alert severity="warning">
          {t('tenantSchema:errors.noSchemaVersionRegistered')}
        </Alert>
      )}

      {baselineNeedsReview && (
        <Alert severity="warning">
          {t('tenantSchema:baseline.artifactsRegisteredButBaselineNeedsReview')}
        </Alert>
      )}

      <SectionCard
        variant="Default"
        title={t('tenantSchemaRunner:title')}
        subtitle={t('tenantSchemaRunner:description')}
        sx={{ mb: 0 }}
      >
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <StatusBadge
            status={runnerEnabled ? 'enabled' : 'disabled'}
            category="generic"
            apiLabel={runnerEnabled ? t('tenantSchemaRunner:runnerEnabled') : t('tenantSchemaRunner:runnerDisabled')}
            size="Sm"
            tone={runnerEnabled ? 'Success' : 'Neutral'}
          />
          <StatusBadge
            status={runnerConfig?.drift_detected ? 'drift' : 'no_drift'}
            category="generic"
            apiLabel={runnerConfig?.drift_detected
              ? t('tenantSchemaRunner:driftDetected')
              : t('tenantSchemaRunner:noDrift')}
            size="Sm"
            tone={runnerConfig?.drift_detected ? 'Warning' : 'Neutral'}
          />
          {baseline && (
            <StatusBadge
              status={baseline.status}
              category="generic"
              apiLabel={`${t('tenantSchema:baseline.title')}: ${baseline.status}`}
              size="Sm"
              tone={baselineApproved ? 'Success' : 'Warning'}
            />
          )}
        </Stack>

        {!runnerEnabled && (
          <Alert severity="info" sx={{ mb: 2 }}>{t('tenantSchema:warnings.runner_disabled')}</Alert>
        )}

        {applyBlocked && canApply && (
          <Alert severity="warning" sx={{ mb: 2 }}>{t('tenantSchemaRunner:applyBlocked')}</Alert>
        )}

        {plan ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
              mb: 1,
            }}
          >
            <DetailField label={t('tenantSchema:tenant_tab.plan_title')}>
              <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 28 }}>
                <StatusBadge status={plan.status} category="schema_plan" size="Sm" />
              </Box>
            </DetailField>
            <DetailField
              label={t('tenantSchema:tenant_tab.included')}
              value={plan.included_migrations_count}
            />
          </Box>
        ) : (
          <Typography sx={{ fontSize: 13, color: semantic.text.muted, mb: 1 }}>
            {t('tenantSchema:tenant_tab.no_plan')}
          </Typography>
        )}

        {plan && !['approved', 'executed'].includes(plan.status) && (
          <Typography sx={{ fontSize: 12, color: semantic.warning, mb: 1 }}>
            {t('tenantSchemaRunner:approvedPlanRequired')}
          </Typography>
        )}

        {latestRun && (
          <Box sx={{ mt: 1.5 }}>
            <Typography sx={{ mb: 1, fontSize: 13, fontWeight: 600, color: semantic.text.primary }}>
              {t('tenantSchemaRunner:latestRun')}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <StatusBadge status={latestRun.status} category="schema_plan" size="Sm" />
              <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
                {formatProvisioningMode(latestRun.mode) || latestRun.mode}
                {' — '}
                {t('tenantSchemaRunner:migrationsPending')}: {latestRun.migrations_pending}
              </Typography>
            </Stack>
          </Box>
        )}
      </SectionCard>

      {canManage && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            variant="Secondary"
            disabled={buildDisabled}
            onClick={() => runAction(() => buildTenantSchemaPlan(tenantId))}
          >
            {t('tenantSchema:actions.build_plan')}
          </Button>
          <Button
            variant="Secondary"
            disabled={buildDisabled || !plan}
            onClick={() => runAction(() => dryRunTenantSchemaPlan(tenantId))}
          >
            {t('tenantSchema:actions.dry_run')}
          </Button>
          {canApprove && plan && (
            <Button
              variant="Danger"
              disabled={!baselineApproved}
              onClick={() => runAction(() => approveTenantSchemaPlan(tenantId, plan.id))}
            >
              {t('tenantSchema:actions.approve_plan')}
            </Button>
          )}
        </Stack>
      )}

      {canRun && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            variant="Secondary"
            disabled={buildDisabled}
            onClick={() => runAction(() => previewTenantSchemaRun(tenantId, { mode: 'dry_run' }))}
          >
            {t('tenantSchemaRunner:preview')}
          </Button>
          <Button
            variant="Primary"
            disabled={buildDisabled}
            onClick={() => runAction(() => startTenantSchemaRun(tenantId, { mode: 'dry_run' }))}
          >
            {t('tenantSchemaRunner:dryRun')}
          </Button>
          {canApply && (
            <Button
              variant="Accent"
              disabled={applyBlocked || buildDisabled}
              onClick={() => runAction(() => startTenantSchemaRun(tenantId, { mode: 'apply' }))}
            >
              {t('tenantSchemaRunner:apply')}
            </Button>
          )}
          {latestRun && ['pending', 'running'].includes(latestRun.status) && (
            <Button
              variant="Danger"
              onClick={() => runAction(() => cancelTenantSchemaRun(tenantId, latestRun.id))}
            >
              {t('tenantSchemaRunner:cancel')}
            </Button>
          )}
        </Stack>
      )}

      {plan?.items && (
        <SectionCard variant="Default" title={t('tenantSchema:tenant_tab.plan_title')} noPadding sx={{ mb: 0 }}>
          <DataTable
            type="Compact"
            embedded
            columns={[
              { id: 'migration_name', label: t('tenantSchema:tenant_tab.columns.migration') },
              { id: 'action', label: t('tenantSchema:tenant_tab.columns.action'), render: (row) => t(`tenantSchema:plan_actions.${row.action}`, row.action) },
              { id: 'risk_level', label: t('tenantSchema:tenant_tab.columns.risk'), render: (row) => <StatusBadge status={row.risk_level} category="schema_risk" size="Sm" /> },
            ]}
            rows={plan.items}
            getRowKey={(row) => row.id}
            emptyMessage={t('tenantSchema:tenant_tab.no_items')}
          />
        </SectionCard>
      )}

      {latestRun?.migrations && (
        <SectionCard
          variant="Default"
          title={`${t('tenantSchemaRunner:migrationsTotal')}: ${latestRun.migrations_total}`}
          noPadding
          sx={{ mb: 0 }}
        >
          <DataTable
            type="Compact"
            embedded
            columns={[
              { id: 'migration_name', label: t('tenantSchema:tenant_tab.columns.migration') },
              { id: 'status', label: t('common:status'), render: (row) => <StatusBadge status={row.status} category="schema_plan" size="Sm" /> },
            ]}
            rows={latestRun.migrations}
            getRowKey={(row) => row.id}
            emptyMessage={t('tenantSchema:tenant_tab.no_items')}
          />
        </SectionCard>
      )}
    </Stack>
  );
}

export function portalSchemaMessage(t, messageKey) {
  const keyMap = {
    customerSafePreparing: 'customerSafePreparing',
    customerSafeReady: 'customerSafeReady',
    customerSafeReadyViaProvisioning: 'customerSafeReadyViaProvisioning',
    customerSafeAttention: 'customerSafeAttention',
    schema_plan_ready: 'customerSafeReady',
    schema_attention_required: 'customerSafeAttention',
    schema_plan_pending: 'customerSafePreparing',
    schema_plan_in_review: 'customerSafePreparing',
  };
  return t(`tenantSchemaRunner:${keyMap[messageKey] || 'customerSafePreparing'}`);
}
